import { Fragment, useMemo } from 'react'
import { Printer, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { IconoFamilia, IconoItem, agruparDesglose, svgFamiliaHTML, svgLineaHTML, labelFamiliaDoc } from '@/lib/cd2bIconos'

/**
 * PreviewCotizacion — el documento que el asesor le ENVÍA al dueño: una propuesta/cotización
 * limpia y profesional con el desglose de lo que va a pagar (mensual + primer pago). NO muestra la
 * comisión del asesor (es interno). Reutilizable en la cotización (empresa nueva) y en la ficha
 * (cambio de paquete de una empresa viva).
 *
 * Acciones: ver en pantalla, Imprimir / Guardar PDF (ventana self-contained → print del navegador),
 * y Copiar resumen (texto plano para pegar en WhatsApp/correo).
 */
export default function PreviewCotizacion({
  open,
  onClose,
  tipo = 'cotizacion',
  empresa = '',
  edicionLabel = '',
  modulosActivos = [],
  desglose = [],
  cargosUnicos = [],
  totalMensual = 0,
  totalUnico = 0,
  moneda = 'USD',
  seats = { completo: 0, operativo: 0 },
  asesor = '',
  descuento = null,
  vigenciaDias = 15,
  // Pago anual (`{ total, ahorro }` que calcula la API) o `null` si el cliente paga cada mes.
  anual = null,
}) {
  const promoMeses = descuento && Number(descuento.mesesPromo) > 0 ? Math.floor(Number(descuento.mesesPromo)) : 0
  const precioFull = descuento?.precioFull || 0
  const [copiado, setCopiado] = useState(false)
  const fecha = useMemo(() => new Date().toLocaleDateString('es-SV', { day: '2-digit', month: 'long', year: 'numeric' }), [])
  const titulo = tipo === 'addenda' ? 'Propuesta de cambio de plan' : 'Cotización'
  const primerPago = (anual ? anual.total : totalMensual) + totalUnico

  const resumenTexto = useMemo(() => {
    const L = []
    L.push(`${titulo} — cd-2b`)
    if (empresa) L.push(`Empresa: ${empresa}`)
    L.push(`Plan: ${edicionLabel}`)
    L.push('')
    L.push('Detalle mensual:')
    for (const g of agruparDesglose(desglose)) {
      L.push(`  ${labelFamiliaDoc(g.tipo)}:`)
      for (const d of g.lineas) L.push(`   • ${d.concepto}${d.detalle ? ` (${d.detalle})` : ''}: ${d.precio > 0 ? fmtMoneda(d.precio, moneda) : 'Incluido'}`)
    }
    L.push(`  Total mensual: ${fmtMoneda(totalMensual, moneda)}`)
    if (cargosUnicos.length) {
      L.push('')
      L.push('Cargos únicos (una sola vez):')
      for (const c of cargosUnicos) L.push(`  • ${c.name || 'Cargo'}: ${fmtMoneda(c.amount, moneda)}`)
      L.push(`  Total único: ${fmtMoneda(totalUnico, moneda)}`)
    }
    L.push('')
    if (anual) {
      L.push(`Pago anual: ${fmtMoneda(primerPago, moneda)}  ·  Un año por adelantado; se renueva cada año`)
      if (anual.ahorro > 0) L.push(`Pagando el año completo ahorras ${fmtMoneda(anual.ahorro, moneda)} frente a pagar mes a mes.`)
    } else {
      L.push(`Primer pago: ${fmtMoneda(primerPago, moneda)}  ·  Luego ${fmtMoneda(totalMensual, moneda)}/mes`)
    }
    if (promoMeses) L.push(`Precio promocional por ${promoMeses} meses; después ${fmtMoneda(precioFull, moneda)}/mes.`)
    L.push(`Usuarios incluidos: ${seats.completo} completos / ${seats.operativo} operativos`)
    if (asesor) L.push(`Atendido por: ${asesor}`)
    L.push(`Válida por ${vigenciaDias} días · ${fecha}`)
    return L.join('\n')
  }, [titulo, empresa, edicionLabel, desglose, cargosUnicos, totalMensual, totalUnico, primerPago, seats, asesor, vigenciaDias, fecha, moneda, promoMeses, precioFull, anual])

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(resumenTexto)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 1800)
    } catch { /* clipboard bloqueado: ignora */ }
  }

  const imprimir = () => {
    const w = window.open('', '_blank', 'width=820,height=1000')
    if (!w) return
    w.document.write(documentoHTML({
      titulo, empresa, edicionLabel, modulosActivos, desglose, cargosUnicos,
      totalMensual, totalUnico, primerPago, seats, asesor, vigenciaDias, fecha, moneda,
      promoMeses, precioFull, anual,
    }))
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 350)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b border-line">
          <DialogTitle className="flex flex-wrap items-center justify-between gap-3">
            <span>Vista previa de la {tipo === 'addenda' ? 'propuesta' : 'cotización'}</span>
            <span className="flex w-full sm:w-auto gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
              <Button size="sm" variant="outline" onClick={copiar}>
                {copiado ? <Check size={15} className="mr-1 text-success-600 dark:text-success-300" /> : <Copy size={15} className="mr-1" />}
                {copiado ? 'Copiado' : 'Copiar'}
              </Button>
              <Button size="sm" onClick={imprimir}><Printer size={15} className="mr-1" /> Imprimir / PDF</Button>
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Documento (espejo de lo que se imprime) */}
        <div className="max-h-[70vh] overflow-y-auto bg-surface-muted p-3 sm:p-6">
          <div className="mx-auto max-w-xl bg-card rounded-lg shadow-sm border border-line p-4 sm:p-8">
            {/* Encabezado */}
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 pb-4 border-b border-neutral-200 dark:border-neutral-800">
              <div>
                <p className="text-h3 font-bold text-primary-700 dark:text-primary-300 tracking-tight">cd-2b</p>
                <p className="text-caption text-neutral-500 dark:text-neutral-400">ERP · Facturación electrónica</p>
              </div>
              <div className="sm:text-right">
                <p className="text-body-sm font-semibold text-neutral-800 dark:text-neutral-200">{titulo}</p>
                <p className="text-caption text-neutral-500 dark:text-neutral-400">{fecha}</p>
              </div>
            </div>

            {/* Cliente / plan */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 py-4">
              <div>
                <p className="text-micro text-neutral-400 dark:text-neutral-500 uppercase mb-0.5">Para</p>
                <p className="text-body-sm font-medium text-neutral-800 dark:text-neutral-200">{empresa || '—'}</p>
              </div>
              <div>
                <p className="text-micro text-neutral-400 dark:text-neutral-500 uppercase mb-0.5">Plan</p>
                <p className="text-body-sm font-medium text-neutral-800 dark:text-neutral-200">{edicionLabel}</p>
              </div>
            </div>

            {modulosActivos.length > 0 && (
              <div className="pb-4">
                <p className="text-micro text-neutral-400 dark:text-neutral-500 uppercase mb-1.5">Incluye</p>
                <div className="flex flex-wrap gap-1.5">
                  {modulosActivos.map((m, i) => (
                    <span key={i} className="text-caption bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded-full px-2 py-0.5">{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Tabla mensual */}
            <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="text-micro text-neutral-400 dark:text-neutral-500 uppercase border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left font-medium py-1.5">Concepto</th>
                  <th className="text-right font-medium py-1.5">Mensual</th>
                </tr>
              </thead>
              <tbody>
                {/* Agrupado por familia: el cliente debe ver de un vistazo qué es sistema, qué es
                    cupo, qué son usuarios y qué es trabajo del asesor. */}
                {agruparDesglose(desglose).map((g) => (
                  <Fragment key={g.tipo}>
                    <tr>
                      <td colSpan={2} className="pt-3 pb-0.5">
                        <span className="inline-flex items-center gap-1.5 text-micro uppercase tracking-wide text-neutral-500 dark:text-neutral-400 font-medium">
                          <IconoFamilia tipo={g.tipo} size={12} /> {g.label}
                        </span>
                      </td>
                    </tr>
                    {g.lineas.map((d, i) => (
                      <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800">
                        <td className="py-1.5 text-neutral-700 dark:text-neutral-300">
                          <IconoItem item={{ icono: d.icono, tipo: g.tipo }} size={12} className="mr-1" />
                          {d.concepto}{d.detalle ? <span className="text-neutral-400 dark:text-neutral-500"> · {d.detalle}</span> : null}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-neutral-800 dark:text-neutral-200">
                          {d.precio > 0 ? fmtMoneda(d.precio, moneda) : <span className="text-success-600 dark:text-success-300">Incluido</span>}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
                <tr>
                  <td className="py-2 font-semibold text-neutral-800 dark:text-neutral-200">Total mensual</td>
                  <td className="py-2 text-right font-bold tabular-nums text-primary-700 dark:text-primary-300">{fmtMoneda(totalMensual, moneda)}</td>
                </tr>
              </tbody>
            </table>
            </div>

            {/* Cargos únicos */}
            {cargosUnicos.length > 0 && (
              <div className="overflow-x-auto mt-4">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="text-micro text-neutral-400 dark:text-neutral-500 uppercase border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left font-medium py-1.5">Cargos únicos (primera factura)</th>
                    <th className="text-right font-medium py-1.5">Una vez</th>
                  </tr>
                </thead>
                <tbody>
                  {cargosUnicos.map((c, i) => (
                    <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800">
                      <td className="py-1.5 text-neutral-700 dark:text-neutral-300">{c.name || 'Cargo'}</td>
                      <td className="py-1.5 text-right tabular-nums text-neutral-800 dark:text-neutral-200">{fmtMoneda(c.amount, moneda)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="py-2 font-semibold text-neutral-800 dark:text-neutral-200">Total único</td>
                    <td className="py-2 text-right font-bold tabular-nums text-neutral-900 dark:text-neutral-50">{fmtMoneda(totalUnico, moneda)}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            )}

            {/* Primer pago destacado */}
            <div className="mt-5 rounded-lg bg-primary-50 dark:bg-primary-900/40 border border-primary-100 dark:border-primary-800 px-4 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div>
                <p className="text-body-sm font-semibold text-neutral-800 dark:text-neutral-200">{anual ? 'Pago anual' : 'Primer pago'}</p>
                <p className="text-caption text-neutral-500 dark:text-neutral-400">
                  {anual ? 'Un año por adelantado; se renueva cada año' : `Luego ${fmtMoneda(totalMensual, moneda)}/mes`}
                </p>
              </div>
              <p className="text-h2 font-bold tabular-nums text-primary-700 dark:text-primary-300">{fmtMoneda(primerPago, moneda)}</p>
            </div>

            {anual && anual.ahorro > 0 && (
              <p className="mt-2 text-caption text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40 rounded-md px-3 py-2">
                Pagando el año completo ahorras <b>{fmtMoneda(anual.ahorro, moneda)}</b> frente a pagar mes a mes.
              </p>
            )}

            {promoMeses > 0 && (
              <p className="mt-2 text-caption text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40 rounded-md px-3 py-2">
                Precio promocional por <b>{promoMeses} meses</b>; después vuelve a <b>{fmtMoneda(precioFull, moneda)}/mes</b>.
              </p>
            )}

            {/* Pie */}
            <div className="mt-5 pt-4 border-t border-neutral-200 dark:border-neutral-800 text-caption text-neutral-500 dark:text-neutral-400 space-y-1">
              <p>Usuarios incluidos: {seats.completo} completos · {seats.operativo} operativos (POS). Precios en {moneda}, no incluyen impuestos aplicables.</p>
              <p>Cotización válida por {vigenciaDias} días.{asesor ? ` Atendido por ${asesor}.` : ''}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** HTML self-contained para imprimir / guardar como PDF (independiente del CSS de la app). */
function documentoHTML(d) {
  const money = (n) => fmtMoneda(n, d.moneda)
  const chips = (d.modulosActivos || []).map((m) => `<span class="chip">${esc(m)}</span>`).join('')
  // Agrupado por familia, igual que el documento que el backend envía por correo y publica en
  // la página de aceptación (`api/src/services/cd2bDesgloseVista.js`).
  const filasMes = agruparDesglose(d.desglose || []).map((g) => `
    <tr class="fam"><td colspan="2"><span class="famlbl">${svgFamiliaHTML(g.tipo)} ${esc(labelFamiliaDoc(g.tipo))}</span></td></tr>`
    + g.lineas.map((l) => `
    <tr><td>${svgLineaHTML(l)} ${esc(l.concepto)}${l.detalle ? ` <span class="muted">· ${esc(l.detalle)}</span>` : ''}</td>
    <td class="num">${l.precio > 0 ? money(l.precio) : '<span class="inc">Incluido</span>'}</td></tr>`).join('')).join('')
  const filasUnico = (d.cargosUnicos || []).map((c) => `
    <tr><td>${esc(c.name || 'Cargo')}</td><td class="num">${money(c.amount)}</td></tr>`).join('')
  const bloqueUnico = (d.cargosUnicos || []).length ? `
    <table>
      <thead><tr><th>Cargos únicos (primera factura)</th><th class="num">Una vez</th></tr></thead>
      <tbody>${filasUnico}
        <tr class="total"><td>Total único</td><td class="num">${money(d.totalUnico)}</td></tr>
      </tbody>
    </table>` : ''
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(d.titulo)} — ${esc(d.empresa || 'cd-2b')}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #1f2430; margin: 0; padding: 32px; background: #fff; }
    .doc { max-width: 640px; margin: 0 auto; }
    .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #e5e7eb; padding-bottom: 14px; }
    .brand { font-size: 26px; font-weight: 800; color: #4338ca; letter-spacing: -0.5px; }
    .sub { font-size: 12px; color: #6b7280; }
    .doct { text-align: right; }
    .doct b { font-size: 14px; }
    .meta { display: flex; gap: 40px; padding: 16px 0; }
    .meta .lbl { font-size: 10px; text-transform: uppercase; color: #9ca3af; letter-spacing: 0.5px; }
    .meta .val { font-size: 14px; font-weight: 600; }
    .chips { padding-bottom: 14px; }
    .chip { display: inline-block; font-size: 11px; background: #eef2ff; color: #4338ca; border-radius: 999px; padding: 2px 9px; margin: 0 4px 4px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 13px; }
    th { text-align: left; font-size: 10px; text-transform: uppercase; color: #9ca3af; letter-spacing: 0.5px; border-bottom: 1px solid #e5e7eb; padding: 6px 0; }
    th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
    td { padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
    tr.total td { font-weight: 700; border-bottom: none; padding-top: 8px; }
    .muted { color: #9ca3af; }
    .inc { color: #16a34a; }
    tr.fam td { padding-top: 12px; padding-bottom: 2px; border: 0; }
    tr.fam:first-child td { padding-top: 2px; }
    .famlbl { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; letter-spacing: .06em;
              text-transform: uppercase; color: #6b7280; font-weight: 600; }
    .totmes td { color: #4338ca; }
    .pago { margin-top: 20px; background: #eef2ff; border: 1px solid #e0e7ff; border-radius: 10px; padding: 14px 18px; display: flex; justify-content: space-between; align-items: center; }
    .pago .big { font-size: 26px; font-weight: 800; color: #4338ca; font-variant-numeric: tabular-nums; }
    .pago .cap { font-size: 12px; color: #6b7280; }
    .foot { margin-top: 20px; padding-top: 14px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; line-height: 1.6; }
    @media print { body { padding: 0; } .doc { max-width: none; } }
  </style></head>
  <body><div class="doc">
    <div class="head">
      <div><div class="brand">cd-2b</div><div class="sub">ERP · Facturación electrónica</div></div>
      <div class="doct"><b>${esc(d.titulo)}</b><div class="sub">${esc(d.fecha)}</div></div>
    </div>
    <div class="meta">
      <div><div class="lbl">Para</div><div class="val">${esc(d.empresa || '—')}</div></div>
      <div><div class="lbl">Plan</div><div class="val">${esc(d.edicionLabel)}</div></div>
    </div>
    ${chips ? `<div class="chips"><div class="lbl" style="font-size:10px;text-transform:uppercase;color:#9ca3af;margin-bottom:6px">Incluye</div>${chips}</div>` : ''}
    <table>
      <thead><tr><th>Concepto</th><th class="num">Mensual</th></tr></thead>
      <tbody>${filasMes}
        <tr class="total totmes"><td>Total mensual</td><td class="num">${money(d.totalMensual)}</td></tr>
      </tbody>
    </table>
    ${bloqueUnico}
    <div class="pago">
      <div><div style="font-weight:600">${d.anual ? 'Pago anual' : 'Primer pago'}</div><div class="cap">${d.anual ? 'Un año por adelantado; se renueva cada año' : `Luego ${money(d.totalMensual)}/mes`}</div></div>
      <div class="big">${money(d.primerPago)}</div>
    </div>
    ${d.anual && d.anual.ahorro > 0 ? `<div style="margin-top:10px;background:#eef2ff;border-radius:8px;padding:8px 12px;font-size:12px;color:#4338ca">Pagando el año completo ahorras <b>${money(d.anual.ahorro)}</b> frente a pagar mes a mes.</div>` : ''}
    ${d.promoMeses ? `<div style="margin-top:10px;background:#eef2ff;border-radius:8px;padding:8px 12px;font-size:12px;color:#4338ca">Precio promocional por <b>${d.promoMeses} meses</b>; después vuelve a <b>${money(d.precioFull)}/mes</b>.</div>` : ''}
    <div class="foot">
      <div>Usuarios incluidos: ${d.seats.completo} completos · ${d.seats.operativo} operativos (POS). Precios en ${esc(d.moneda)}, no incluyen impuestos aplicables.</div>
      <div>Cotización válida por ${d.vigenciaDias} días.${d.asesor ? ` Atendido por ${esc(d.asesor)}.` : ''}</div>
    </div>
  </div></body></html>`
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}
